/**
 * Step 5: Confirm & Create
 *
 * Single mode: Final review + create one candidate.
 * Bulk mode: Summary + create all included candidates.
 */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../../services/recruitingApi";
import type { WizardState, BulkFileResult } from "./CandidateImportWizard";

interface Props {
  wizardState: WizardState;
  onBack: () => void;
}

interface BulkCreateResult {
  index: number;
  status: "success" | "error";
  candidateId?: number;
  name: string;
  error?: string;
  errorDetail?: string;
}

const Step5_Confirm: React.FC<Props> = ({ wizardState, onBack }) => {
  const { t } = useTranslation("recruiting");
  const navigate = useNavigate();
  const isBulk = wizardState.importMethod === "bulk-cv";

  // Single mode state
  const [error, setError] = useState<string | null>(null);

  // Bulk mode state
  const [bulkCreateResults, setBulkCreateResults] = useState<
    BulkCreateResult[] | null
  >(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRowExpand = (index: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Single mode mutation
  const createMutation = useMutation(
    (data: any) => recruitingAPI.finalizeImport(data),
    {
      onSuccess: (response) => {
        navigate(`/candidates/${response.data.id}`);
      },
      onError: (error: any) => {
        const serverMessage = error.response?.data?.error;
        setError(
          serverMessage ||
            error.message ||
            t("importWizard.step5.createFailed"),
        );
      },
    },
  );

  const handleSingleConfirm = () => {
    createMutation.mutate({
      candidateData: wizardState.parsedCandidate,
      fileName: wizardState.uploadedFile?.name,
      source: "cv-import",
    });
  };

  // Bulk mode mutation using React Query
  const bulkCreateMutation = useMutation(
    (data: { candidates: any[] }) => recruitingAPI.finalizeBulkImport(data),
    {
      onSuccess: (response) => {
        setBulkCreateResults(response.results);
      },
      onError: (err: any) => {
        const serverMessage = err.response?.data?.error;
        setError(
          serverMessage ||
            err.message ||
            t("importWizard.step5.createBulkFailed"),
        );
      },
    },
  );

  const handleBulkConfirm = () => {
    const included = (wizardState.bulkResults || []).filter(
      (r) => r.included && r.parsedCandidate,
    );
    if (included.length === 0) return;

    setError(null);
    const candidates = included.map((r: BulkFileResult) => ({
      candidateData: r.parsedCandidate,
      fileName: r.fileName,
      source: "cv-import-bulk",
    }));

    bulkCreateMutation.mutate({ candidates });
  };

  // Bulk mode render
  if (isBulk) {
    const included = (wizardState.bulkResults || []).filter(
      (r) => r.included && r.parsedCandidate,
    );

    // Show results after creation
    if (bulkCreateResults) {
      const createdCount = bulkCreateResults.filter(
        (r) => r.status === "success",
      ).length;
      const failedCount = bulkCreateResults.filter(
        (r) => r.status === "error",
      ).length;

      return (
        <Box>
          <Typography variant="h5" gutterBottom>
            {t("importWizard.step5.importComplete")}
          </Typography>

          <Alert
            severity={failedCount === 0 ? "success" : "warning"}
            sx={{ mb: 3 }}
          >
            {t("importWizard.step5.createdCount", { count: createdCount })}
            {failedCount > 0 &&
              ` ${t("importWizard.step5.failedCount", { count: failedCount })}`}
          </Alert>

          <TableContainer sx={{ maxHeight: 300, mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("importWizard.step5.colStatus")}</TableCell>
                  <TableCell>{t("importWizard.step5.colName")}</TableCell>
                  <TableCell>{t("importWizard.step5.colDetails")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkCreateResults.map((result) => (
                  <TableRow key={result.index}>
                    <TableCell>
                      <Chip
                        label={
                          result.status === "success"
                            ? t("importWizard.step5.created")
                            : t("importWizard.step5.failed")
                        }
                        color={
                          result.status === "success" ? "success" : "error"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{result.name}</TableCell>
                    <TableCell>
                      {result.status === "success" ? (
                        `ID: ${result.candidateId}`
                      ) : (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="error">
                              {result.error}
                            </Typography>
                            {result.errorDetail &&
                              result.errorDetail !== result.error && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRowExpand(result.index)}
                                  sx={{ p: 0.25 }}
                                >
                                  {expandedRows.has(result.index) ? (
                                    <ExpandLess fontSize="small" />
                                  ) : (
                                    <ExpandMore fontSize="small" />
                                  )}
                                </IconButton>
                              )}
                          </Box>
                          {result.errorDetail &&
                            result.errorDetail !== result.error && (
                              <Collapse in={expandedRows.has(result.index)}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    mt: 0.5,
                                    fontFamily: "monospace",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {result.errorDetail}
                                </Typography>
                              </Collapse>
                            )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={() => navigate("/candidates")}>
              {t("importWizard.step5.goToCandidates")}
            </Button>
          </Box>
        </Box>
      );
    }

    // Show confirmation before creation
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {t("importWizard.step5.confirmBulkTitle")}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t("importWizard.step5.creatingFromUploads", {
            count: included.length,
            total: (wizardState.bulkResults || []).length,
          })}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {bulkCreateMutation.isLoading && (
          <Box sx={{ mb: 3 }}>
            <LinearProgress sx={{ height: 6, borderRadius: 3, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t("importWizard.step5.creatingCandidates")}
            </Typography>
          </Box>
        )}

        <TableContainer sx={{ maxHeight: 300, mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("importWizard.step5.colName")}</TableCell>
                <TableCell>{t("importWizard.step5.colEmail")}</TableCell>
                <TableCell>{t("importWizard.step5.colMainSkill")}</TableCell>
                <TableCell>{t("importWizard.step5.colSeniority")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {included.map((result: BulkFileResult) => (
                <TableRow key={result.index}>
                  <TableCell>
                    {result.parsedCandidate?.first_name}{" "}
                    {result.parsedCandidate?.last_name}
                  </TableCell>
                  <TableCell>{result.parsedCandidate?.email || "-"}</TableCell>
                  <TableCell>
                    {result.parsedCandidate?.main_skill || "-"}
                  </TableCell>
                  <TableCell>
                    {result.parsedCandidate?.seniority || "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={onBack}
            disabled={bulkCreateMutation.isLoading}
            variant="outlined"
          >
            {t("importWizard.step5.backToReview")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBulkConfirm}
            disabled={bulkCreateMutation.isLoading || included.length === 0}
            data-testid="create-candidates-button"
          >
            {bulkCreateMutation.isLoading
              ? t("importWizard.step5.creating")
              : t("importWizard.step5.createCandidates", {
                  count: included.length,
                })}
          </Button>
        </Box>
      </Box>
    );
  }

  // Single mode render (existing behavior)
  const candidate = wizardState.parsedCandidate;

  if (!candidate) {
    return (
      <Box>
        <Alert severity="error">{t("importWizard.step5.noDataFound")}</Alert>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          {t("importWizard.step5.back")}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("importWizard.step5.confirmTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("importWizard.step5.reviewBeforeCreate")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {candidate.first_name} {candidate.last_name}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.email")}</strong>{" "}
              {candidate.email || t("importWizard.step5.na")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.phone")}</strong>{" "}
              {candidate.phone || t("importWizard.step5.na")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.location")}</strong>{" "}
              {candidate.location || t("importWizard.step5.na")}
            </Typography>
            {candidate.linkedin_url && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("importWizard.step5.linkedIn")}</strong>{" "}
                <a
                  href={candidate.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {candidate.linkedin_url}
                </a>
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.role")}</strong>{" "}
              {candidate.role_type || t("importWizard.step5.na")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.seniority")}</strong>{" "}
              {candidate.seniority || t("importWizard.step5.na")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.mainSkill")}</strong>{" "}
              {candidate.main_skill || t("importWizard.step5.na")}
            </Typography>
            {candidate.expected_rate && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("importWizard.step5.expectedRate")}</strong> $
                {candidate.expected_rate}/day
              </Typography>
            )}
            {candidate.notice_period && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t("importWizard.step5.noticePeriod")}</strong>{" "}
                {candidate.notice_period}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              <strong>{t("importWizard.step5.englishLevel")}</strong>{" "}
              {candidate.english_level || t("importWizard.step5.na")}
            </Typography>
          </Box>

          {candidate.summary && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t("importWizard.step5.summary")}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {candidate.summary}
              </Typography>
            </Box>
          )}

          {candidate.tags && candidate.tags.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t("importWizard.step5.tags")}</strong>
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {candidate.tags.map((tag: string, index: number) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          onClick={onBack}
          disabled={createMutation.isLoading}
          variant="outlined"
        >
          {t("importWizard.step5.backToEdit")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSingleConfirm}
          disabled={createMutation.isLoading}
          data-testid="create-candidate-button"
        >
          {createMutation.isLoading
            ? t("importWizard.step5.creating")
            : t("importWizard.step5.createCandidate")}
        </Button>
      </Box>
    </Box>
  );
};

export default Step5_Confirm;
